import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiFetch } from './api';

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('hf_token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('hf_token');
    localStorage.removeItem('hf_user');
    setToken(null);
    setUser(null);
  }, []);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    apiFetch('/api/auth/me')
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error('Invalid token');
      })
      .then((data) => {
        setUser(data.user);
      })
      .catch(() => {
        logout();
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token, logout]);

  // Listen for 401 events from apiFetch. api.js no longer touches
  // localStorage directly — it dispatches an `hf_auth_expired` event with
  // the token that triggered the 401 in `detail.token`. We check that
  // against the currently-stored token before clearing: if the user has
  // already re-authenticated (e.g. login race), the fresh token in
  // localStorage will differ from the failed one and we leave it alone.
  useEffect(() => {
    const handleExpired = (event) => {
      const failedToken = event?.detail?.token;
      const currentToken = localStorage.getItem('hf_token');
      if (failedToken && currentToken && failedToken !== currentToken) {
        // User already has a newer token — the 401 was for the old session.
        return;
      }
      localStorage.removeItem('hf_token');
      localStorage.removeItem('hf_user');
      setToken(null);
      setUser(null);
    };
    window.addEventListener('hf_auth_expired', handleExpired);
    return () => window.removeEventListener('hf_auth_expired', handleExpired);
  }, []);

  const login = async (username, password) => {
    const res = await apiFetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Login failed');
    }
    localStorage.setItem('hf_token', data.token);
    localStorage.setItem('hf_user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const register = async (username, password, email = null) => {
    const res = await apiFetch('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, password, email }),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Registration failed');
    }
    localStorage.setItem('hf_token', data.token);
    localStorage.setItem('hf_user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const updateProfile = async (fields) => {
    const res = await apiFetch('/api/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(fields),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Update failed');
    setUser(data.user);
    localStorage.setItem('hf_user', JSON.stringify(data.user));
    return data;
  };

  const value = { user, token, loading, login, register, logout, updateProfile };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
